import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetCart } from "../../redux/hejalSlice";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import toast from "react-hot-toast";
import {
  ChevronLeft,
  Loader2, PlusCircle,
  HomeIcon,
  CreditCard,
  Check,
  Wallet,
  IndianRupee,
  Truck, ShoppingBag
} from "lucide-react";

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract data from navigation state with proper fallbacks
  const cartSummary = location.state?.cartSummary || null;
  const appliedCouponId = location.state?.appliedCoupon || null;
  const cartItems = location.state?.cartItems || [];
  
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(true);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [shippingCharge, setShippingCharge] = useState(0);
  const [distanceData, setDistanceData] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [showFreeProductModal, setShowFreeProductModal] = useState(false);

  // Get user information from Redux store
  const isProfileLoading = useSelector((state) => state.hejal.profileLoading);
  const isLoggedIn = useSelector((state) => state.hejal.isLoggedIn);
  const userProfile = useSelector((state) => state.hejal.userInfo);

  // Debug logging
  console.log('Cart Items for Checkout:', cartItems);
  console.log('Cart Summary:', cartSummary);

  // Redirect if not logged in or no cart items
  useEffect(() => {
    if (isProfileLoading) return;
    
    if (!isLoggedIn) {
      navigate("/signin", { state: { from: "/checkout" } });
      return;
    }

    // Enhanced validation for cart data
    if (!cartSummary || !cartItems || cartItems.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
      return;
    }

    // Fetch addresses
    fetchAddresses();
  }, [isLoggedIn, navigate, cartSummary, cartItems, isProfileLoading]);

  // Fetch user addresses
  const fetchAddresses = async () => {
    setAddressLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAddress,
      });

      if (response.success && response.data) {
        setAddresses(response.data);
        if (response.data.length > 0) {
          setSelectedAddress(response.data[0]);
          calculateDistance(response.data[0]._id);
        }
      } else {
        toast.error(response.message || "Failed to fetch addresses");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load addresses");
    } finally {
      setAddressLoading(false);
    }
  };

  // Calculate distance and shipping charges
  const calculateDistance = async (addressId) => {
    setCalculatingDistance(true);
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: `${apiPaths.calculateDistance}/${addressId}`,
      });

      if (response.success && response.data) {
        setDistanceData(response.data);
        const distance = parseFloat(response.data.distance);
        const perKmPrice = response.data.perKmPrice;
        const shipping = distance * perKmPrice;
        setShippingCharge(shipping);
      } else {
        toast.error(response.message || "Failed to calculate delivery distance");
        setShippingCharge(0);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to calculate shipping charge");
      setShippingCharge(0);
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Handle address selection
  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    calculateDistance(address._id);
  };

  // Handle payment method selection
  const handleSelectPaymentMethod = (method) => {
    setPaymentMethod(method);
  };

  // Navigate to add new address
  const navigateToAddAddress = () => {
    navigate("/profile", { state: { returnTo: "/checkout" } });
  };

  // Go back to cart
  const goBackToCart = () => {
    navigate("/cart");
  };

  // Helper function to get item display name
  const getItemDisplayName = (item) => {
    if (item.itemType === 'combo') {
      return `${item.comboDetails?.comboName || item.product?.name} (Combo)`;
    }
    return item.product?.name || 'Unknown Item';
  };

  // Get safe cart summary values - SYNCED WITH BACKEND STRUCTURE
  const getSafeCartSummary = () => {
    if (!cartSummary) {
      return {
        itemCount: 0,
        totalQuantity: 0,
        subtotal: 0,
        total: 0,
        discount: 0
      };
    }

    // Backend response structure: { totalItems, totalQuantity, totalAmount, items }
    // finalTotals structure: { subtotal, discount, total }
    return {
      itemCount: cartSummary.totalItems || 0,
      totalQuantity: cartSummary.totalQuantity || 0,
      subtotal: cartSummary.finalTotals?.subtotal || cartSummary.totalAmount || 0,
      total: cartSummary.finalTotals?.total || cartSummary.totalAmount || 0,
      discount: cartSummary.finalTotals?.discount || 0
    };
  };

  const safeCartSummary = getSafeCartSummary();

  // Calculate total amount including shipping
  const calculateTotal = () => {
    const subtotal = safeCartSummary.total || 0; // Use final total (after discounts)
    const shipping = shippingCharge || 0;
    const platformFees = distanceData?.platformFees || 0;
    return subtotal + shipping + platformFees;
  };

  // Handle Razorpay payment
  function onLoadScipt(src) {
    return new Promise((resolve) => {
      const ScriptTag = document.createElement("script");
      ScriptTag.src = src;

      ScriptTag.onload = () => {
        resolve(true);
      };

      ScriptTag.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(ScriptTag);
    });
  }

  const handleRazorpayPayment = async (orderId, amount, notes) => {
    const res = await onLoadScipt("https://checkout.razorpay.com/v1/checkout.js");
    
    if (!res) {
      setProcessingOrder(false);
      toast.error("Razorpay SDK failed to load. Please try again.");
      return;
    }

    try {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: "INR",
        name: "Glaxy",
        description: "Payment for your order",
        order_id: orderId,
        handler: function (response) {
          if (response.error) {
            setProcessingOrder(false);
            return;
          }
          verifyPayment(response, notes?.orderId);
        },
        modal: {
          ondismiss: function () {
            setProcessingOrder(false);
          },
        },
        prefill: {
          name: userProfile?.fullName || "",
          email: userProfile?.email || "",
          contact: userProfile?.phoneNumber || "",
        },
        theme: {
          color: "#40b8ec",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      setProcessingOrder(false);
      toast.error(
        err.response?.data?.message || "Payment initialization failed"
      );
    }
  };

  // Verify payment
  const verifyPayment = async (paymentResponse, orderId) => {
    const toastId = toast.loading("Verifying payment...");
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.verifyPayment,
        data: {
          orderId,
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_order_id: paymentResponse.razorpay_order_id,
          razorpay_signature: paymentResponse.razorpay_signature,
        },
      });

      if (response.success) {
        setOrderSuccess(true);
        dispatch(resetCart());
        navigate("/orders");
        toast.dismiss(toastId);
        toast.success("Payment successful! Order placed.");
      } else {
        toast.dismiss(toastId);
        toast.error(response.message || "Payment verification failed");
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.message || "Payment verification failed");
    } finally {
      setProcessingOrder(false);
    }
  };

  // Place order - ENHANCED WITH COMPLETE CART DATA
  const placeOrder = async (freeProductStockIgnore = false) => {
    if (!selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }

    setProcessingOrder(true);

    try {
      // Enhanced order payload with complete cart information
      const orderPayload = {
        addressId: selectedAddress._id,
        couponCode: appliedCouponId ? appliedCouponId : null,
        paymentType: paymentMethod,
        freeProductStockIgnore: freeProductStockIgnore,
        // Include cart data if backend needs it for validation
        cartItems: cartItems,
        cartSummary: cartSummary,
        shippingCharge: shippingCharge,
        platformFees: distanceData?.platformFees || 0,
        finalTotal: calculateTotal()
      };

      console.log('Order Payload:', orderPayload);

      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.createOrder,
        data: orderPayload,
      });

      if (response.success) {
        setOrderDetails(response.data);

        // If payment method is Razorpay, initialize payment
        if (paymentMethod === "razorpay") {
          await handleRazorpayPayment(response.data.id, response.data.amount, response.data.notes);
        } else {
          setProcessingOrder(false);
          dispatch(resetCart());
          navigate("/orders");
          toast.success("Order created successfully!");
        }
      } else {
        setProcessingOrder(false);
        toast.error(response.message || "Failed to create order");
      }
    } catch (err) {
      console.log("Order creation error:", err);
      if (err?.response?.data?.data?.freeProductsStockIsOver === true) {
        setShowFreeProductModal(true);
      } else {
        setProcessingOrder(false);
        toast.error(err?.response?.data?.message || "Failed to create order");
      }
    }
  };

  const continueWithoutFreeProducts = () => {
    setShowFreeProductModal(false);
    placeOrder(true); 
  };

  const cancelOrderPlacement = () => {
    setShowFreeProductModal(false);
    setProcessingOrder(false);
    toast.success("Order placement cancelled");
  };

  if (isLoading || addressLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 size={36} className="animate-spin text-secondColor" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <button
        onClick={goBackToCart}
        className="flex items-center gap-2 text-secondColor hover:underline mb-6"
      >
        <ChevronLeft size={16} />
        Back to Cart
      </button>

      <h1 className="text-2xl md:text-3xl font-bold mb-8">Checkout</h1>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Delivery Address */}
        <div className="lg:w-2/3 w-full">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <HomeIcon size={20} />
                Select Delivery Address
              </h2>
              <button
                onClick={navigateToAddAddress}
                className="text-[#182134] flex items-center gap-1 hover:underline"
              >
                <PlusCircle size={16} />
                Add New Address
              </button>
            </div>

            {addresses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No saved addresses found</p>
                <button
                  onClick={navigateToAddAddress}
                  className="px-4 py-2 bg-secondColor text-white rounded-md hover:bg-green-600 transition-all"
                >
                  Add New Address
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((address) => (
                  <div
                    key={address._id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedAddress?._id === address._id
                        ? "border-secondColor bg-green-50"
                        : "hover:border-gray-400"
                    }`}
                    onClick={() => handleSelectAddress(address)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-grow">
                        <p className="font-medium">
                          {address.houseNumber}, {address.societyName}
                        </p>
                        <p className="text-gray-600 text-sm mt-1">
                          {address.street}, {address.city}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {address.state}, {address.pinCode}
                        </p>
                      </div>
                      {selectedAddress?._id === address._id && (
                        <Check size={20} className="text-secondColor" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
              <CreditCard size={20} />
              Select Payment Method
            </h2>
            <div className="space-y-4">
              {/* Cash on Delivery */}
              <div
                className={`p-4 border rounded-lg cursor-pointer ${
                  paymentMethod === "cod"
                    ? "border-secondColor bg-green-50"
                    : "bg-gray-50 hover:border-gray-400"
                }`}
                onClick={() => handleSelectPaymentMethod("cod")}
              >
                <div className="flex items-center gap-3">
                  <IndianRupee size={20} className="text-secondColor" />
                  <div className="flex-grow">
                    <p className="font-medium">Cash on Delivery</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Pay with cash when your order is delivered.
                    </p>
                  </div>
                  {paymentMethod === "cod" && (
                    <Check size={20} className="text-secondColor" />
                  )}
                </div>
              </div>

              {/* Razorpay */}
              <div
                className={`p-4 border rounded-lg cursor-pointer ${
                  paymentMethod === "razorpay"
                    ? "border-secondColor bg-green-50"
                    : "bg-gray-50 hover:border-gray-400"
                }`}
                onClick={() => handleSelectPaymentMethod("razorpay")}
              >
                <div className="flex items-center gap-3">
                  <Wallet size={20} className="text-secondColor" />
                  <div className="flex-grow">
                    <p className="font-medium">Pay Online</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Pay securely using Razorpay. Credit/Debit cards, UPI, and
                      more.
                    </p>
                  </div>
                  {paymentMethod === "razorpay" && (
                    <Check size={20} className="text-secondColor" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:w-1/3 w-full">
          <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>

            {/* ITEMS BREAKDOWN */}
            <div className="mb-4 max-h-40 overflow-y-auto">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Items in your order:</h3>
              {cartItems.map((item) => (
                <div key={item._id} className="flex justify-between text-xs py-1 border-b last:border-b-0">
                  <span className="truncate max-w-[180px]">
                    {getItemDisplayName(item)} (×{item.quantity})
                  </span>
                  <span className="font-medium">₹{(item.totals?.itemTotal || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between pb-3 border-b">
                <span className="text-gray-600">
                  Subtotal ({safeCartSummary.totalQuantity} items)
                </span>
                <span>₹{safeCartSummary.subtotal.toLocaleString()}</span>
              </div>

              {safeCartSummary.discount > 0 && (
                <div className="flex justify-between pb-3 border-b text-green-600">
                  <span>Discount Applied</span>
                  <span>-₹{safeCartSummary.discount.toLocaleString()}</span>
                </div>
              )}

              <div className="flex justify-between pb-3 border-b">
                <span className="text-gray-600">
                  After Discounts
                </span>
                <span className="font-medium">₹{safeCartSummary.total.toLocaleString()}</span>
              </div>

              <div className="flex justify-between pb-3 border-b">
                <span className="text-gray-600 flex items-center gap-1">
                  <Truck size={16} />
                  Shipping
                  {calculatingDistance && (
                    <Loader2 size={14} className="animate-spin ml-1" />
                  )}
                </span>
                <span>
                  {distanceData ? (
                    <span className="flex items-center">
                      ₹{shippingCharge.toLocaleString()}
                    </span>
                  ) : (
                    <span>₹{shippingCharge.toLocaleString()}</span>
                  )}
                </span>
              </div>

              <div className="flex justify-between pb-3 border-b">
                <span className="text-gray-600 flex items-center gap-1">
                  <ShoppingBag size={16} />
                  Platform Fee
                  {calculatingDistance && (
                    <Loader2 size={14} className="animate-spin ml-1" />
                  )}
                </span>
                <span>
                  {distanceData ? (
                    <span className="flex items-center">
                      ₹{(distanceData?.platformFees || 0).toLocaleString()}
                    </span>
                  ) : (
                    <span>₹0</span>
                  )}
                </span>
              </div>

              <div className="flex justify-between font-bold text-lg pt-3 bg-blue-50 px-3 py-2 rounded-lg">
                <span>Final Total</span>
                <span className="text-blue-600">₹{calculateTotal().toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={() => placeOrder()}
              disabled={
                !selectedAddress || processingOrder || addresses.length === 0 || calculatingDistance
              }
              className="w-full bg-[#182134] text-white py-3 rounded-lg font-medium hover:bg-[#182134] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processingOrder ? (
                <>
                  <Loader2 size={20} className="animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                `Place Order${paymentMethod === "razorpay" ? " & Pay" : ""}`
              )}
            </button>

            <p className="text-sm text-gray-500 text-center mt-3">
              {paymentMethod === "razorpay"
                ? "You will be redirected to Razorpay to complete your payment."
                : "You will pay when your order is delivered."}
            </p>
          </div>
        </div>
      </div>

      {/* FREE PRODUCT MODAL */}
      {showFreeProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Free Products Unavailable</h3>
            <p className="mb-6">Free products are currently out of stock. Would you like to continue your order without the free products?</p>
            <div className="flex justify-end gap-4">
              <button 
                onClick={cancelOrderPlacement}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button 
                onClick={continueWithoutFreeProducts}
                className="px-4 py-2 bg-secondColor text-white rounded-md hover:bg-green-600"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;